import * as THREE from 'three'
import Experience from './Experience.js'
import vertexShader from './Shaders/backgroundVertex.glsl'
import fragmentShader from './Shaders/backgroundFragment.glsl'

export default class Background {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.sizes = this.experience.sizes
        this.camera = this.experience.camera.instance
        this.time = this.experience.time
        this.debug = this.experience.debug
        this.resources = this.experience.resources

        // Trail configuration
        this.trailSize = 256
        this.mouseRadius = 0.04 // Radius of the mouse effect (0.1 = 10% of the smaller dimension)
        this.distortionStrength = 50 // Strength of the distortion effect
        this.trailFalloff = 0.97 // How quickly the trail fades (higher = longer trails)
        this.fadeSpeed = 0.08 // How quickly the canvas fades between frames
        this.returnSpeed = 0.08 // How quickly positions return to normal

        // Mouse state tracking
        this.lastMouseX = 999
        this.lastMouseY = 999
        this.isMouseMoving = false
        this.mouseTimeout = null
        this.fadeOutSpeed = 0.05 // Controls how quickly the blob fades out
        this.currentGlowStrength = 0
        
        // Animation flag - always animate the blob when visible
        this.animateBlob = true

        // Start by loading the glow texture
        this.setupTrailCanvas()
        this.setGeometry()
        this.setMaterial()
        this.setMesh()
        
        // Add pointer event listener
        window.addEventListener('pointermove', this.onPointerMove.bind(this))
        
        // Add resize listener to update mesh size when viewport changes
        this.sizes.on('resize', () => {
            this.updateMeshSize()
        })
        
        // Initial mesh size update
        this.updateMeshSize()
        
        // Add debug controls if available
        this.setupDebugControls()
    }

    loadGlowTexture() {
        // Load the glow.png image from the direct URL
        const textureLoader = new THREE.TextureLoader()
        textureLoader.load(
            'https://cdn.prod.website-files.com/64892891a9277d495f1c6ba2/67c856c4a6b6836a5c71aaab_glow.png',
            (glowTexture) => {
                this.glowTexture = glowTexture
                this.glowTexture.minFilter = THREE.LinearFilter
                this.glowTexture.magFilter = THREE.LinearFilter
                
                // Now that we have the glow texture, set up the trail canvas
                this.setupTrailCanvas()
                
                // Set up the rest of the background
                this.setGeometry()
                this.setMaterial()
                this.setMesh()
                
                // Add pointer event listener
                window.addEventListener('pointermove', this.onPointerMove.bind(this))
                
                // Add resize listener - only update the aspect ratio uniform
                this.sizes.on('resize', () => {
                    if (this.material && this.material.uniforms.uAspectRatio) {
                        this.material.uniforms.uAspectRatio.value = this.sizes.width / this.sizes.height
                        this.material.uniforms.uResolution.value.set(this.sizes.width, this.sizes.height)
                    }
                })
                
                // Add debug controls if available
                this.setupDebugControls()
            },
            undefined,
            (error) => {
                console.error('Error loading glow texture:', error)
                
                // Fallback to a simple canvas setup without the glow texture
                this.setupTrailCanvas()
                this.setGeometry()
                this.setMaterial()
                this.setMesh()
                window.addEventListener('pointermove', this.onPointerMove.bind(this))
                this.sizes.on('resize', () => this.updateMeshSize())
                this.setupDebugControls()
            }
        )
    }

    setupDebugControls() {
        if (this.debug && this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder('Background')
            
            this.debugFolder.add(this, 'mouseRadius', 0.01, 0.2, 0.01)
                .name('Mouse Radius')
                .onChange(() => {
                    this.material.uniforms.uMouseRadius.value = this.mouseRadius
                })
            
            this.debugFolder.add(this, 'distortionStrength', 0.01, 0.5, 0.01)
                .name('Distortion Strength')
                .onChange(() => {
                    this.material.uniforms.uDisplacementStrength.value = this.distortionStrength
                })
            
            this.debugFolder.add(this, 'trailFalloff', 0.5, 0.99, 0.01)
                .name('Trail Persistence')
                .onChange(() => {
                    this.material.uniforms.uTrailFalloff.value = this.trailFalloff
                })
            
            this.debugFolder.add(this, 'fadeSpeed', 0.01, 0.2, 0.01)
                .name('Fade Speed')
                
            this.debugFolder.add(this, 'returnSpeed', 0.1, 0.99, 0.01)
                .name('Return Speed')
                .onChange(() => {
                    this.material.uniforms.uReturnSpeed.value = this.returnSpeed
                })
        }
    }

    setupTrailCanvas() {
        // Create a canvas for the trail effect
        this.canvas = document.createElement('canvas');
        
        // Use a higher resolution for smoother details
        this.canvas.width = 3024; // Increased from 512
        this.canvas.height = 3024; // Increased from 512
        
        this.ctx = this.canvas.getContext('2d');
        this.ctx.fillStyle = 'black';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Create a texture from the canvas
        this.trailTexture = new THREE.CanvasTexture(this.canvas);
        this.trailTexture.minFilter = THREE.LinearFilter;
        this.trailTexture.magFilter = THREE.LinearFilter;
        
        // Initialize raycaster for UV coordinate calculations
        this.raycaster = new THREE.Raycaster();
        
        // Create a custom glow texture
        this.createGlowTexture();
        
        // Draw initial off-screen position
        this.drawGlowAtPosition(999, 999);
    }

    createGlowTexture() {
        // Create a canvas for the glow texture
        const glowCanvas = document.createElement('canvas')
        const size = 256 // Power of 2 for better performance
        glowCanvas.width = size
        glowCanvas.height = size
        
        const ctx = glowCanvas.getContext('2d')
        
        // Create a radial gradient for the glow
        const gradient = ctx.createRadialGradient(
            size / 2, size / 2, 0,
            size / 2, size / 2, size / 2
        )
        
        // Add color stops for a nice glow effect
        gradient.addColorStop(0, 'rgba(180, 100, 255, 1)') // Bright purple at center
        gradient.addColorStop(0.3, 'rgba(120, 60, 220, 0.8)') // Medium purple
        gradient.addColorStop(0.7, 'rgba(80, 20, 180, 0.4)') // Dark purple
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)') // Transparent at edge
        
        // Fill the canvas with the gradient
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, size, size)
        
        // Create a texture from the canvas
        this.customGlowTexture = new THREE.CanvasTexture(glowCanvas)
        this.customGlowTexture.minFilter = THREE.LinearFilter
        this.customGlowTexture.magFilter = THREE.LinearFilter
    }

    drawGlowAtPosition(x, y, strength) {
        // Apply a fade effect based on the trail falloff
        const fadeAmount = 1 - this.trailFalloff;
        this.ctx.fillStyle = `rgba(0, 0, 0, ${fadeAmount})`;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
        // Check if position is off-screen
        const isOffScreen = x > this.canvas.width || y > this.canvas.height || x < 0 || y < 0;
    
        if (!isOffScreen && strength > 0) {
            // Calculate the size of the glow in pixels
            const pixelRadius = this.mouseRadius * Math.min(this.canvas.width, this.canvas.height);
            
            // Save context state and apply blending
            this.ctx.save();
            this.ctx.globalAlpha = strength;
            this.ctx.globalCompositeOperation = 'lighter'; // Soft blending
    
            // Start a new path for the blob
            this.ctx.beginPath();
            
            // Get the current time for animation
            const time = this.time.elapsed * 0.001;
            
            // Number of points to create the blob (more points = smoother blob)
            const numPoints = 32; // Increased for smoothness
            
            // Maximum variation from perfect circle
            const maxVariation = pixelRadius * 0.15;
    
            // Draw the blob as a series of curves
            for (let i = 0; i <= numPoints; i++) {
                const angle = (i / numPoints) * Math.PI * 2;
                const variation = 
                    Math.sin(angle * 3 + time * 0.2) * 0.3 + 
                    Math.sin(angle * 5 - time * 0.1) * 0.2 +
                    Math.sin(angle * 7 + time * 0.05) * 0.1;
                const clampedVariation = Math.max(-0.5, Math.min(0.5, variation)) * maxVariation;
                const radius = pixelRadius + clampedVariation;
                const px = x + Math.cos(angle) * radius;
                const py = y + Math.sin(angle) * radius;
    
                if (i === 0) {
                    this.ctx.moveTo(px, py);
                } else {
                    this.ctx.lineTo(px, py);
                }
            }
            
            // Close the path
            this.ctx.closePath();
            
            // Create a radial gradient for the blob
            const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, pixelRadius);
            gradient.addColorStop(0, 'rgba(255, 255, 255, 1.0)');
            gradient.addColorStop(0.6, 'rgba(255, 255, 255, 0.6)');
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
            
            // Fill the blob with the gradient
            this.ctx.fillStyle = gradient;
            this.ctx.fill();
            
            this.ctx.restore();
        }
        
        // Update the texture
        this.trailTexture.needsUpdate = true;
    }

    setGeometry() {
        // Create a plane geometry with a fixed aspect ratio
        // We'll scale it in updateMeshSize()
        this.geometry = new THREE.PlaneGeometry(10, 10, 120, 120)
    }

    setMaterial() {
        this.material = new THREE.ShaderMaterial({
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            uniforms: {
                uTime: { value: 0 },
                uTrailTexture: { value: this.trailTexture },
                uTrailFalloff: { value: this.trailFalloff },
                uDistortionStrength: { value: this.distortionStrength * 3.0 },
                uReturnSpeed: { value: this.returnSpeed },
                uResolution: { value: new THREE.Vector2(this.sizes.width, this.sizes.height) },
                uAspectRatio: { value: this.sizes.width / this.sizes.height }
            },
            transparent: true
        })
    }

    setMesh() {
        this.mesh = new THREE.Mesh(this.geometry, this.material)
        
        // Position the mesh behind other elements
        this.mesh.position.z = -2
        this.mesh.renderOrder = -1
        
        this.scene.add(this.mesh)
    }

    onPointerMove(event) {
        // Get normalized device coordinates
        const ndcX = (event.clientX / this.sizes.width) * 2 - 1
        const ndcY = -(event.clientY / this.sizes.height) * 2 + 1
        
        // Use raycasting to get exact UV coordinates
        this.raycaster.setFromCamera({ x: ndcX, y: ndcY }, this.camera)
        const intersects = this.raycaster.intersectObject(this.mesh)
        
        if (intersects.length > 0) {
            const uv = intersects[0].uv
            
            // Calculate pixel positions
            const x = uv.x * this.canvas.width
            const y = (1 - uv.y) * this.canvas.height
            
            // Store the last mouse position
            this.lastMouseX = x
            this.lastMouseY = y
            
            // Set flag to indicate mouse is moving
            this.isMouseMoving = true
            
            // Set the glow strength to maximum
            this.currentGlowStrength = this.distortionStrength
            
            // Draw the blob at the current position
            this.drawGlowAtPosition(x, y, this.currentGlowStrength)
            
            // Clear any existing timeout
            if (this.mouseTimeout) {
                clearTimeout(this.mouseTimeout)
            }
            
            // Set a timeout to detect when mouse stops moving
            this.mouseTimeout = setTimeout(() => {
                this.isMouseMoving = false
            }, 100) // Short timeout to detect when mouse stops
        }
    }

    update() {
        // Only update if the material has been created
        if (this.material && this.material.uniforms) {
            // Update time uniform
            this.material.uniforms.uTime.value = this.time.elapsed * 0.001
            
            // Update other uniforms if they need to be changed dynamically
            if (this.material.uniforms.uTrailFalloff) {
                this.material.uniforms.uTrailFalloff.value = this.trailFalloff
            }
            
            if (this.material.uniforms.uDistortionStrength) {
                this.material.uniforms.uDistortionStrength.value = this.distortionStrength
            }
            
            if (this.material.uniforms.uReturnSpeed) {
                this.material.uniforms.uReturnSpeed.value = this.returnSpeed
            }
            
            // When mouse is moving, draw the animated blob
            if (this.isMouseMoving) {
                this.drawGlowAtPosition(this.lastMouseX, this.lastMouseY, this.currentGlowStrength)
            } 
            // When mouse stops, just apply the fade effect without drawing new blobs
            else if (!this.isMouseMoving && this.currentGlowStrength > 0) {
                // Apply a stronger fade effect when not moving
                const fadeAmount = 0.1; // Stronger fade (adjust as needed)
                this.ctx.fillStyle = `rgba(0, 0, 0, ${fadeAmount})`
                this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
                this.trailTexture.needsUpdate = true
                
                // Reduce the glow strength
                this.currentGlowStrength = Math.max(0, this.currentGlowStrength - this.fadeOutSpeed)
            }
        }
    }

    updateMeshSize() {
        if (!this.mesh || !this.camera) return
        
        // Calculate the size needed to cover the viewport at the mesh's z-position
        const distance = Math.abs(this.mesh.position.z - this.camera.position.z)
        const fov = this.camera.fov * (Math.PI / 180)
        const height = 2 * Math.tan(fov / 2) * distance
        const width = height * this.camera.aspect
        
        // Add some padding to ensure full coverage (1.1 = 10% extra)
        const padding = 1.1
        
        // Update the mesh scale
        // this.mesh.scale.set(width * padding, height * padding, 1)
        
        // Update uniforms related to viewport size
        if (this.material && this.material.uniforms) {
            if (this.material.uniforms.uResolution) {
                this.material.uniforms.uResolution.value.set(this.sizes.width, this.sizes.height)
            }
            
            if (this.material.uniforms.uAspectRatio) {
                this.material.uniforms.uAspectRatio.value = this.sizes.width / this.sizes.height
            }
        }
        
        // Log the new size for debugging
        console.log(`Mesh scaled to: ${width * padding} x ${height * padding}`)
    }
}